import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, FileSpreadsheet, FileText, FileImage, Play, Eye, ExternalLink, Trash2, HardDriveUpload, Search } from "lucide-react";
import { tarifasApi, errMsg, fmtDate } from "./api";
import { UploadBox } from "./UploadBox";

const NONE = "__none__";

const STATUS = {
  new: ["Nuevo", "bg-blue-100 text-blue-800"],
  modified: ["Modificado", "bg-amber-100 text-amber-800"],
  processing: ["Procesando…", "bg-violet-100 text-violet-800"],
  processed: ["Procesado", "bg-emerald-100 text-emerald-800"],
  error: ["Error", "bg-red-100 text-red-800"],
};

export const DriveFiles = ({ providers, googleConnected, onOpenFile, refreshKey }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      setFiles(await tarifasApi.files());
    } catch (e) {
      toast.error(errMsg(e, "No se pudieron cargar los archivos"));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load, refreshKey]);

  // Polling mientras haya archivos procesándose
  useEffect(() => {
    const busy = files.some((f) => f.status === "processing");
    clearTimeout(timer.current);
    if (busy) timer.current = setTimeout(load, 3000);
    return () => clearTimeout(timer.current);
  }, [files, load]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await tarifasApi.scan();
      toast.success(`Drive revisado: ${r.new} nuevos, ${r.modified} modificados, ${r.unchanged} sin cambios (${r.total} archivos)`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo revisar Google Drive"));
    } finally {
      setScanning(false);
    }
  };

  const assign = async (f, pid) => {
    try {
      await tarifasApi.assignFile(f.id, pid === NONE ? null : pid);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo asignar el proveedor"));
    }
  };

  const process = async (f) => {
    try {
      await tarifasApi.processFile(f.id);
      toast.success(`Procesando "${f.name}"… (lectura con IA, puede tardar 1-2 min)`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo iniciar el procesamiento"));
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`¿Eliminar "${f.name}" y su extracción? (no afecta a precios ya actualizados en KimKim)`)) return;
    try {
      await tarifasApi.deleteFile(f.id);
      toast.success("Archivo eliminado");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };

  const provName = (id) => providers.find((p) => p.id === id)?.name;
  const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const q = norm(query.trim());
  const visible = files
    .filter((f) => filter === "all" || (filter === "pending" ? ["new", "modified", "error"].includes(f.status) : filter === "upload" ? f.source === "upload" : f.status === filter))
    .filter((f) => !q || norm(f.name).includes(q) || norm(f.path).includes(q) || norm(provName(f.provider_id)).includes(q));
  const icon = (f) => {
    if (/\.(png|jpe?g|webp|heic|heif|tiff?|gif|bmp)$/i.test(f.name)) return <FileImage className="h-4 w-4 text-sky-700 shrink-0" />;
    if (f.mime_type.includes("sheet") || /\.(xls|csv)/i.test(f.name)) return <FileSpreadsheet className="h-4 w-4 text-emerald-700 shrink-0" />;
    return <FileText className="h-4 w-4 text-red-700 shrink-0" />;
  };

  return (
    <Card className="rounded-md" data-testid="drive-files-card">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold">Archivos de tarifas</h3>
          <p className="text-xs text-muted-foreground">De Google Drive (se detectan nuevos y modificados en todas las subcarpetas) o subidos desde el ordenador. Nada se escribe en KimKim hasta que pulses "Actualizar" en el detalle.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input data-testid="files-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por proveedor, archivo o carpeta…" className="h-8 w-64 pl-7 rounded-md text-xs" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="files-filter" className="h-8 w-48 rounded-md text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Nuevos / modificados / error</SelectItem>
              <SelectItem value="processed">Procesados</SelectItem>
              <SelectItem value="upload">Subidos desde el ordenador</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="drive-scan-button" size="sm" className="rounded-md" onClick={scan} disabled={scanning || !googleConnected}>
            {scanning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />} Revisar Drive ahora
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <UploadBox providers={providers} onUploaded={load} />
        {!googleConnected && <p className="text-sm text-amber-700" data-testid="files-need-google">Google Drive no está conectado: mientras tanto puedes importar los tarifarios desde el ordenador.</p>}
        {loading && <p className="text-sm text-muted-foreground py-3 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>}
        {!loading && files.length === 0 && <p className="text-sm text-muted-foreground py-3" data-testid="files-empty">Aún no hay archivos. Pulsa "Revisar Drive ahora" o sube un tarifario desde el ordenador.</p>}
        {!loading && files.length > 0 && visible.length === 0 && <p className="text-sm text-muted-foreground py-3" data-testid="files-no-results">Ningún archivo coincide con la búsqueda o el filtro.</p>}
        {!loading && q && visible.length > 0 && <p className="text-xs text-muted-foreground" data-testid="files-result-count">{visible.length} de {files.length} archivos</p>}
        <div className="divide-y">
          {visible.map((f) => {
            const [label, cls] = STATUS[f.status] || [f.status, "bg-slate-100 text-slate-700"];
            const link = f.source === "upload" ? tarifasApi.downloadUrl(f.id) : f.web_view_link;
            return (
              <div key={f.id} className="py-2.5 flex flex-wrap items-center gap-3" data-testid={`file-row-${f.id}`}>
                {icon(f)}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate" data-testid={`file-name-${f.id}`}>{f.name}</span>
                    <Badge className={`rounded-md ${cls} hover:${cls.split(" ")[0]}`} data-testid={`file-status-${f.id}`}>{label}</Badge>
                    {f.source === "upload" && <Badge className="rounded-md bg-sky-50 text-sky-800 hover:bg-sky-50" data-testid={`file-source-${f.id}`}><HardDriveUpload className="h-3 w-3 mr-1" /> subido</Badge>}
                    {!f.supported && <Badge className="rounded-md bg-slate-100 text-slate-600 hover:bg-slate-100">formato no soportado</Badge>}
                    {link && <a href={link} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" data-testid={`file-link-${f.id}`}><ExternalLink className="h-3 w-3" /></a>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{f.path || "(raíz)"} · {f.source === "upload" ? `subido por ${f.uploaded_by || "admin"} ` : `modificado ${fmtDate(f.modified_time)} · v${f.version}`}{f.processed_at ? ` · procesado ${fmtDate(f.processed_at)}` : ""}</p>
                  {f.status_message && <p className={`text-xs ${f.status === "error" ? "text-red-700" : "text-violet-700"}`} data-testid={`file-message-${f.id}`}>{f.status_message}</p>}
                </div>
                <Select value={f.provider_id || NONE} onValueChange={(v) => assign(f, v)}>
                  <SelectTrigger data-testid={`file-provider-${f.id}`} className="h-8 w-48 rounded-md text-xs"><SelectValue placeholder="Proveedor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— sin proveedor —</SelectItem>
                    {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid={`file-process-${f.id}`} size="sm" variant={f.status === "processed" ? "outline" : "default"} className="rounded-md"
                  disabled={!f.supported || !f.provider_id || f.status === "processing"} onClick={() => process(f)} title={!provName(f.provider_id) ? "Asigna un proveedor" : ""}>
                  <Play className="h-3 w-3 mr-1" /> {f.status === "processed" ? "Reprocesar" : "Procesar"}
                </Button>
                <Button data-testid={`file-open-${f.id}`} size="sm" variant="outline" className="rounded-md" disabled={!f.extraction_id} onClick={() => onOpenFile(f)}>
                  <Eye className="h-3 w-3 mr-1" /> Ver
                </Button>
                {f.source === "upload" && (
                  <Button data-testid={`file-delete-${f.id}`} size="sm" variant="ghost" className="rounded-md text-red-600" disabled={f.status === "processing"} onClick={() => remove(f)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
